/**
 * Leave Request Component for Employees
 * Submit and manage leave requests
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leaveAPI } from '@/lib/api';
import { 
  Plus, Calendar, Clock, CheckCircle, XCircle, Loader2, 
  CalendarDays, AlertCircle, Trash2
} from 'lucide-react';

const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual Leave', color: 'bg-blue-100 text-blue-700' },
  { value: 'sick', label: 'Sick Leave', color: 'bg-red-100 text-red-700' },
  { value: 'personal', label: 'Personal Leave', color: 'bg-purple-100 text-purple-700' },
  { value: 'unpaid', label: 'Unpaid Leave', color: 'bg-gray-100 text-gray-700' }
];

export const LeaveRequestComponent = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({});
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // New request form
  const [newRequest, setNewRequest] = useState({
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: '',
    half_day: false,
    half_day_period: ''
  });

  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  const fetchLeaveRequests = async () => {
    try {
      const res = await leaveAPI.getMy();
      setRequests(res.data.requests || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.leave_type || !newRequest.start_date || !newRequest.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!newRequest.half_day && !newRequest.end_date) {
      toast.error('Please select an end date');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...newRequest,
        end_date: newRequest.half_day ? newRequest.start_date : newRequest.end_date
      };
      await leaveAPI.create(payload);
      toast.success('Leave request submitted successfully!');
      setShowNewRequest(false);
      setNewRequest({
        leave_type: '',
        start_date: '',
        end_date: '',
        reason: '',
        half_day: false,
        half_day_period: ''
      });
      fetchLeaveRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (leaveId) => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) return;
    
    try {
      await leaveAPI.cancel(leaveId);
      toast.success('Leave request cancelled');
      fetchLeaveRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
      approved: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
      cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return styles[status] || styles.pending;
  };

  const getLeaveTypeBadge = (type) => {
    const leaveType = LEAVE_TYPES.find(t => t.value === type);
    return leaveType?.color || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { 
        day: 'numeric', month: 'short', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Summary */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-5 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <CalendarDays className="w-7 h-7" />
              <h2 className="text-xl font-bold">Leave Requests</h2>
            </div>
            <p className="text-purple-100 text-sm">Request and manage your time off</p>
          </div>
          <Button
            onClick={() => setShowNewRequest(true)}
            className="bg-white text-purple-700 hover:bg-purple-50"
            data-testid="new-leave-request-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
        </div>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-purple-400/30">
          <div className="text-center">
            <p className="text-2xl font-bold">{summary.total_requests || 0}</p>
            <p className="text-xs text-purple-200">Total</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{summary.pending || 0}</p>
            <p className="text-xs text-purple-200">Pending</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{summary.approved || 0}</p>
            <p className="text-xs text-purple-200">Approved</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{summary.total_approved_days || 0}</p>
            <p className="text-xs text-purple-200">Days Taken</p>
          </div>
        </div>
      </div>

      {/* Leave Requests List */}
      {requests.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-8 text-center">
          <CalendarDays className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">No Leave Requests</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">You have not submitted any leave requests yet.</p>
          <Button onClick={() => setShowNewRequest(true)} className="bg-teal-600 text-white hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" />
            Submit Your First Request
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(request => (
            <div 
              key={request.id}
              className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 hover:border-purple-300 transition-colors"
              data-testid={`leave-request-${request.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge className={getLeaveTypeBadge(request.leave_type)}>
                      {LEAVE_TYPES.find(t => t.value === request.leave_type)?.label || request.leave_type}
                    </Badge>
                    <Badge className={getStatusBadge(request.status)}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </Badge>
                    {request.half_day && (
                      <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                        Half Day ({request.half_day_period})
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(request.start_date)}
                      {request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {request.days_requested} day{request.days_requested !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 mt-2 text-sm">{request.reason}</p>
                  
                  {request.admin_notes && (
                    <div className="mt-2 p-2 bg-gray-50 dark:bg-slate-700 rounded text-sm">
                      <p className="text-gray-600 dark:text-gray-400">
                        <strong>Admin Notes:</strong> {request.admin_notes}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                {request.status === 'pending' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCancelRequest(request.id)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Leave Request Dialog */}
      <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
        <DialogContent className="bg-white dark:bg-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-purple-600" />
              New Leave Request
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select 
                value={newRequest.leave_type}
                onValueChange={(val) => setNewRequest({ ...newRequest, leave_type: val })}
              >
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="half-day"
                checked={newRequest.half_day}
                onCheckedChange={(checked) => setNewRequest({ 
                  ...newRequest, 
                  half_day: checked,
                  end_date: checked ? '' : newRequest.end_date
                })}
              />
              <Label htmlFor="half-day" className="cursor-pointer">Half day leave</Label>
            </div>
            
            {newRequest.half_day && (
              <div className="space-y-2">
                <Label>Period *</Label>
                <Select 
                  value={newRequest.half_day_period}
                  onValueChange={(val) => setNewRequest({ ...newRequest, half_day_period: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (First Half)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (Second Half)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{newRequest.half_day ? 'Date *' : 'Start Date *'}</Label>
                <Input
                  type="date"
                  value={newRequest.start_date}
                  onChange={(e) => setNewRequest({ ...newRequest, start_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="leave-start-date"
                />
              </div>
              {!newRequest.half_day && (
                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={newRequest.end_date}
                    onChange={(e) => setNewRequest({ ...newRequest, end_date: e.target.value })}
                    min={newRequest.start_date || new Date().toISOString().split('T')[0]}
                    data-testid="leave-end-date"
                  />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Textarea
                value={newRequest.reason}
                onChange={(e) => setNewRequest({ ...newRequest, reason: e.target.value })}
                placeholder="Briefly describe the reason for your leave..."
                className="min-h-[80px]"
                data-testid="leave-reason"
              />
            </div>
            
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-300 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Your request will be reviewed by an administrator. You will be notified once it is approved or rejected.
              </p>
            </div>
            
            <Button
              onClick={handleSubmitRequest}
              disabled={submitting}
              className="w-full bg-purple-600 text-white hover:bg-purple-700"
              data-testid="submit-leave-request"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveRequestComponent;
